/**
 * Pull-Down / Late Scheduling
 * 
 * After initial BFS layer assignment, nodes are "pulled down" to sit as close
 * as possible to their children. This prevents a root node that connects to a
 * node deep in a chain from being stranded at layer 0 with a very long edge.
 * 
 * BALANCED PULL-DOWN:
 * A node is only pulled down when doing so reduces total edge span. The node
 * is placed at the position that minimises the longer of its two edges
 * (parent→node and node→child). For parentless nodes (true roots that were
 * placed at layer 0), the old behaviour is preserved: pull as close to
 * children as possible. For nodes WITH parents, the node is placed at the
 * midpoint between its parent constraint and its earliest child, so that
 * neither edge becomes excessively long.
 * 
 * Iterates in forward layer order so that pulling down a parent cascades correctly.
 */

/**
 * Pull nodes down to minimize long edges.
 * Mutates `layers` and `taskLayers` in place.
 * 
 * @param {string[][]} layers - Array of layer arrays (node IDs)
 * @param {Map<string, number>} taskLayers - Map of nodeId -> layer index
 * @param {Object} graph - Dependency graph with .children, .parents
 * @param {Set<string>} feedbackNodes - Set of feedback node IDs to exclude from parent checks
 */
export function pullDownNodes(layers, taskLayers, graph, feedbackNodes) {
  let changed = true;
  let pullIterations = 0;
  const maxPullIterations = layers.length + 1;
  
  while (changed && pullIterations < maxPullIterations) {
    changed = false;
    pullIterations++;
    
    // Process nodes from the earliest layers (most likely to need pulling down)
    for (let li = 0; li < layers.length; li++) {
      const layer = layers[li];
      const nodesToPull = [];
      
      for (const nodeId of layer) {
        // Find the minimum layer of this node's forward children
        const children = graph.children.get(nodeId) || [];
        const childLayers = children
          .filter(cid => taskLayers.has(cid))
          .map(cid => taskLayers.get(cid));
        
        if (childLayers.length === 0) continue; // No children to pull toward
        
        const minChildLayer = Math.min(...childLayers);
        
        // Find the maximum layer of this node's forward parents
        const parents = graph.parents.get(nodeId) || [];
        const parentLayers = parents
          .filter(pid => taskLayers.has(pid) && !feedbackNodes.has(pid))
          .map(pid => taskLayers.get(pid));
        
        const maxParentLayer = parentLayers.length > 0
          ? Math.max(...parentLayers)
          : -1;
        const minAllowedLayer = maxParentLayer + 1;
        
        let idealLayer;
        
        if (parentLayers.length === 0) {
          // Parentless node (true root or pulled-down root): pull as close to
          // children as possible — the original behaviour. These nodes have no
          // upstream edge to stretch.
          idealLayer = Math.max(minAllowedLayer, minChildLayer - 1);
        } else {
          // Node WITH parents: balance the parent→node and node→child edges.
          // Place at the midpoint (rounded down) between the parent constraint
          // and the child constraint, so neither edge is excessively long.
          const closestToChild = minChildLayer - 1;
          if (closestToChild <= minAllowedLayer) {
            // Already as close to child as parent constraint allows
            idealLayer = minAllowedLayer;
          } else {
            // Pick the midpoint between parent constraint and child
            idealLayer = Math.floor((minAllowedLayer + closestToChild) / 2);
            // Ensure we don't go below the minimum allowed
            idealLayer = Math.max(idealLayer, minAllowedLayer);
          }
        }
        
        if (idealLayer > li) {
          nodesToPull.push({ nodeId, idealLayer });
        }
      }
      
      // Move nodes to their new layers
      for (const { nodeId, idealLayer } of nodesToPull) {
        // Remove from current layer
        const currentLayerArr = layers[li];
        const idx = currentLayerArr.indexOf(nodeId);
        if (idx !== -1) {
          currentLayerArr.splice(idx, 1);
        }
        
        // Ensure target layer exists
        while (layers.length <= idealLayer) {
          layers.push([]);
        }
        
        // Add to new layer
        layers[idealLayer].push(nodeId);
        taskLayers.set(nodeId, idealLayer);
        changed = true;
      }
    }
  }
  
  // Clean up: remove any empty layers and re-index
  const nonEmptyLayers = layers.filter(l => l.length > 0);
  if (nonEmptyLayers.length !== layers.length) {
    // Rebuild layers array and update taskLayers
    layers.length = 0;
    nonEmptyLayers.forEach((layer, newIndex) => {
      layers.push(layer);
      layer.forEach(nodeId => taskLayers.set(nodeId, newIndex));
    });
  }
}
